// src/components/Admin/UserManager/PointLogs.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, CircularProgress, TextField,
    FormControl, InputLabel, Select, MenuItem,
    TableContainer, Paper, Table, TableHead,
    TableRow, TableCell, TableBody, Pagination,
} from '@mui/material';
import dayjs from 'dayjs';
import api from '../../../api/axios';

// ✅ 1. 설명 번역 맵 추가
const DESCRIPTION_MAP = {
    'UNLOCK:widget.reactionBar': '미니홈 응원 위젯 해금',
    'UNLOCK:theme.background.emote': '미니홈 이모티콘 배경 해금',
    'UNLOCK:widget.titleShowcase': '미니홈 칭호 진열장 해금',
    'UNLOCK:widget.emoteShelf': '미니홈 이모티콘 진열장 해금',
    'UNLOCK:widget.textBox': '미니홈 텍스트 박스 해금'
};

// ✅ 2. 설명 변환 함수 추가
const formatDescription = (description) => {
    return DESCRIPTION_MAP[description] || description || '-';
};

const TYPE_KO = { earn: '지급', use: '차감' };
const PAGE_SIZE = 15;

export default function PointLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    /* 클라이언트 필터·정렬 상태 */
    const [search, setSearch] = useState('');
    const [typeFilt, setTypeFilt] = useState('all');
    const [sortBy, setSortBy] = useState('date_desc');
    const [page, setPage] = useState(1);

    /* 데이터 최초 로딩 */
    useEffect(() => {
        api.get('/api/management/point-logs/')
            .then((res) => {
                const data = res.data.results ?? res.data;
                setLogs(
                    data.slice().sort((a, b) => new Date(b.date) - new Date(a.date)),
                );
            })
            .catch((err) => {
                console.error('포인트(Leaf) 로그 조회 실패:', err);
                alert('포인트(Leaf) 로그를 불러오는 데 실패했습니다.');
            })
            .finally(() => setLoading(false));
    }, []);

    /* 필터·정렬 적용 */
    const processed = useMemo(() => {
        let arr = logs;

        if (search.trim()) {
            const q = search.toLowerCase();
            arr = arr.filter((l) =>
                (l.user || '').toLowerCase().includes(q)
                // ✅ 변환된 한글 설명으로 검색하도록 수정
                || formatDescription(l.description).toLowerCase().includes(q)
            );
        }
        if (typeFilt !== 'all') arr = arr.filter((l) => l.point_type === typeFilt);

        return [...arr].sort((a, b) => {
            if (sortBy === 'date_desc') return new Date(b.date) - new Date(a.date);
            if (sortBy === 'date_asc') return new Date(a.date) - new Date(b.date);
            if (sortBy === 'amount_desc') return b.amount - a.amount;
            if (sortBy === 'amount_asc') return a.amount - b.amount;
            return 0;
        });
    }, [logs, search, typeFilt, sortBy]);

    useEffect(() => setPage(1), [search, typeFilt, sortBy, logs]);

    const totalPages = Math.max(1, Math.ceil(processed.length / PAGE_SIZE));
    const paginated = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return processed.slice(start, start + PAGE_SIZE);
    }, [processed, page]);

    if (loading) {
        return <CircularProgress sx={{ display: 'block', mt: 4, mx: 'auto' }} />;
    }

    return (
        <Box>
            {/* 검색·필터·정렬 UI */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    label="유저/설명"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                <FormControl size="small">
                    <InputLabel>구분</InputLabel>
                    <Select
                        value={typeFilt}
                        label="구분"
                        onChange={(e) => setTypeFilt(e.target.value)}
                    >
                        <MenuItem value="all">전체</MenuItem>
                        <MenuItem value="earn">지급</MenuItem>
                        <MenuItem value="use">차감</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small">
                    <InputLabel>정렬 기준</InputLabel>
                    <Select
                        value={sortBy}
                        label="정렬 기준"
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        <MenuItem value="date_desc">최신순</MenuItem>
                        <MenuItem value="date_asc">과거순</MenuItem>
                        <MenuItem value="amount_desc">금액 높은 순</MenuItem>
                        <MenuItem value="amount_asc">금액 낮은 순</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* 테이블 */}
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    '& tr:hover': { bgcolor: 'grey.100' },
                    '& td, & th': {
                        borderBottom: '1px solid #eee',
                        textAlign: 'center',
                        p: 1.25,
                        whiteSpace: 'nowrap',
                        /* 모바일: 패딩·폰트 축소 */
                        '@media (max-width:600px)': {
                            p: 0.75,
                            fontSize: '0.8rem',
                        },
                    },
                    '& th': { bgcolor: 'grey.50', fontWeight: 'bold' },
                    /* 모바일: 테이블 전체 축소 & 스크롤 */
                    '@media (max-width:600px)': {
                        transform: 'scale(0.9)',
                        transformOrigin: 'top left',
                        width: 'max-content',
                        overflowX: 'auto',
                    },
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell width={160}>날짜</TableCell>
                            <TableCell width={140}>유저</TableCell>
                            <TableCell width={120}>구분</TableCell>
                            <TableCell width={120}>금액</TableCell>
                            <TableCell width={260}>설명</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginated.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} sx={{ py: 5 }}>
                                    🔍 조건에 맞는 로그가 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginated.map((log, idx) => (
                                <TableRow
                                    key={log.id ?? `${log.date}-${log.user}-${idx}`}
                                >
                                    <TableCell>
                                        {dayjs(log.date).format('YYYY-MM-DD HH:mm')}
                                    </TableCell>
                                    <TableCell>{log.user || '-'}</TableCell>
                                    <TableCell>
                                        {TYPE_KO[log.point_type] || log.point_type}
                                    </TableCell>
                                    <TableCell>
                                        {`${log.amount > 0 ? '+' : ''}${log.amount.toLocaleString()} Leaf🌿`}
                                    </TableCell>
                                    {/* ✅ formatDescription 함수 사용 */}
                                    <TableCell>{formatDescription(log.description)}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 2,
                    /* 모바일: 배율 맞추기 */
                    '@media (max-width:600px)': { transform: 'scale(0.9)' },
                }}
                >
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        showFirstButton
                        showLastButton
                        shape="rounded"
                        color="primary"
                    />
                </Box>
            )}
        </Box>
    );
}
